// ─────────────────────────────────────────────────────────────────────────────
// JediSwitcher, the artist-surface "view as role" control. Port of role-perms.js
// ensureSwitcherDom/renderSwitcherDropdown. Renders ONLY for the artist jedi
// (whitsettband@gmail.com, by email, not by role string). Lets the jedi preview
// the app as any artist-side seat to QA the role gates; picking a role reloads
// the app with that seat simulated (see lib/viewAs.ts applyViewAs).
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { User } from '../../lib';
import { isArtistJedi, getViewAs, setViewAs, getViewPlan, setViewPlan, VIEW_AS_OPTIONS } from '../../lib';

const wrapStyle: CSSProperties = { position: 'relative' };

const btnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 'var(--ac-radius-pill, 999px)',
  border: '1px solid var(--ac-color-accent, #8B1A2B)',
  background: 'var(--ac-color-accent, #8B1A2B)',
  color: 'var(--ac-color-on-accent, #fff)',
  fontFamily: 'var(--ac-font-display, inherit)',
  fontSize: 12,
  letterSpacing: '0.04em',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const ddStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  minWidth: 220,
  background: 'var(--ac-color-paper, #fff)',
  border: '1px solid var(--ac-color-line, #e3e3e1)',
  borderRadius: 12,
  boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
  padding: 6,
  zIndex: 1000,
};

const headStyle: CSSProperties = {
  padding: '8px 10px 6px',
  fontSize: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--ac-color-t2, #6b6b68)',
  fontFamily: 'var(--ac-font-display, inherit)',
};

const itemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '9px 10px',
  borderRadius: 8,
  border: 'none',
  background: 'transparent',
  color: 'var(--ac-color-ink, #0e0c0b)',
  fontSize: 13,
  cursor: 'pointer',
  textAlign: 'left',
};

export function JediSwitcher({ user }: { readonly user: User | null }): JSX.Element | null {
  // Gate by EMAIL, which applyViewAs preserves, so the switcher stays visible
  // even while viewing-as a non-jedi seat (otherwise the jedi would be trapped
  // in the simulated role with no way back to god mode).
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isArtistJedi(user)) return null;

  const current = getViewAs(); // null === god mode
  const activeKey = current || 'jedi';
  /* acp-preview 2026-07-24: plan dimension rides the same dropdown. */
  const planOn = getViewPlan() === 'acp';
  const label = (current ? 'AS ' + current.replace(/_/g, ' ').toUpperCase() : 'JEDI') + (planOn ? ' \u00b7 ACP' : '');

  return (
    <div className="jedi-switcher" style={wrapStyle} ref={rootRef}>
      <button type="button" style={btnStyle} aria-expanded={open} title="View as role" onClick={() => setOpen((o) => !o)}>
        <span>{label}</span>
        <span aria-hidden style={{ fontSize: 9 }}>▾</span>
      </button>
      {open && (
        <div style={ddStyle} role="menu">
          <div style={headStyle}>View as role</div>
          {VIEW_AS_OPTIONS.map((opt) => {
            const isActive = activeKey === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="menuitem"
                style={{
                  ...itemStyle,
                  background: isActive ? 'var(--ac-color-wash, #f2f2f1)' : 'transparent',
                  fontWeight: isActive ? 700 : 400,
                }}
                onClick={() => {
                  setOpen(false);
                  // No-op if already on this view (avoid a pointless reload).
                  if ((getViewAs() || 'jedi') === opt.key) return;
                  setViewAs(opt.key === 'jedi' ? null : opt.key);
                }}
              >
                <span>{opt.label}</span>
                {isActive && <span style={{ fontSize: 11, color: 'var(--ac-color-accent, #8B1A2B)' }}>●</span>}
              </button>
            );
          })}
          <div style={headStyle}>View plan</div>
          {([
            { key: 'ac', label: 'Standard (AC)' },
            { key: 'acp', label: 'Artist CNTRL Pro' },
          ] as const).map((opt) => {
            const isActive = planOn ? opt.key === 'acp' : opt.key === 'ac';
            return (
              <button
                key={opt.key}
                type="button"
                role="menuitem"
                style={{
                  ...itemStyle,
                  background: isActive ? 'var(--ac-color-wash, #f2f2f1)' : 'transparent',
                  fontWeight: isActive ? 700 : 400,
                }}
                onClick={() => {
                  setOpen(false);
                  if (isActive) return; // no-op, avoid a pointless reload
                  setViewPlan(opt.key === 'acp' ? 'acp' : null);
                }}
              >
                <span>{opt.label}</span>
                {isActive && <span style={{ fontSize: 11, color: 'var(--ac-color-accent, #8B1A2B)' }}>●</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
